import { Panel } from "@/components/ui-bits";

/**
 * End-of-day production rollup, grouped by recipe.
 * Lives on the Finished Inventory page (the manager's end-of-day landing point).
 *
 * Backend returns per-recipe: planned, produced, packed, remaining_packs,
 * unpacked_batches — plus a top-level `unpacked_batches` array enumerating
 * each un-packed batch (the trays still sitting in the cooling rack).
 */
export function DailyTotalsPanel({ totals, loading }) {
  const day = totals?.date;
  const unpackedList = totals?.unpacked_batches || [];
  return (
    <>
      <Panel
        title={`End of day totals · ${day || "today"}`}
        action={
          <span className="text-xs uppercase tracking-wider text-stone-500">
            {totals?.totals?.batch_count ?? 0} batch
            {(totals?.totals?.batch_count ?? 0) === 1 ? "" : "es"}
          </span>
        }
      >
        {loading ? (
          <div className="py-6 text-center text-stone-500 text-sm">Loading totals…</div>
        ) : !totals || totals.products.length === 0 ? (
          <div className="py-6 text-center text-stone-500 text-sm" data-testid="eod-empty">
            No production started today.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]" data-testid="eod-table">
              <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="py-2">Product</th>
                  <th className="text-right">Batches</th>
                  <th className="text-right">Planned</th>
                  <th className="text-right">Produced</th>
                  <th className="text-right">Packed</th>
                  <th className="text-right">Unpacked</th>
                  <th className="text-right">Stock left</th>
                </tr>
              </thead>
              <tbody>
                {totals.products.map((p) => (
                  <tr
                    key={p.recipe_id || p.recipe_name}
                    className="border-t border-border h-12"
                    data-testid={`eod-row-${p.recipe_id || "unknown"}`}
                  >
                    <td className="font-medium">{p.recipe_name}</td>
                    <td className="text-right tnum">{p.batch_count}</td>
                    <td className="text-right tnum text-stone-600">
                      {Number(p.planned || 0).toFixed(2)}
                    </td>
                    <td className="text-right tnum font-semibold">
                      {Number(p.produced || 0).toFixed(2)}
                    </td>
                    <td className="text-right tnum text-stone-700">
                      {Number(p.packed || 0).toFixed(0)}
                    </td>
                    <td
                      className={`text-right tnum font-semibold ${
                        (p.unpacked_batches || 0) > 0 ? "text-amber-700" : "text-stone-400"
                      }`}
                    >
                      {p.unpacked_batches || 0}
                    </td>
                    <td className="text-right tnum text-stone-700">
                      {Number(p.remaining_packs || 0).toFixed(0)}
                    </td>
                  </tr>
                ))}
                <tr
                  className="border-t-2 border-stone-300 h-12 bg-stone-50/60"
                  data-testid="eod-totals"
                >
                  <td className="font-bold uppercase text-xs tracking-wider">Total</td>
                  <td className="text-right tnum font-bold">{totals.totals.batch_count}</td>
                  <td className="text-right tnum font-bold">
                    {Number(totals.totals.planned || 0).toFixed(2)}
                  </td>
                  <td className="text-right tnum font-bold">
                    {Number(totals.totals.produced || 0).toFixed(2)}
                  </td>
                  <td className="text-right tnum font-bold">
                    {Number(totals.totals.packed || 0).toFixed(0)}
                  </td>
                  <td className="text-right tnum font-bold text-amber-700">
                    {totals.totals.unpacked_batches || 0}
                  </td>
                  <td className="text-right tnum font-bold">
                    {Number(totals.totals.remaining_packs || 0).toFixed(0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {unpackedList.length > 0 && (
        <Panel
          title={`Un-packed batches (${unpackedList.length})`}
          action={
            <span className="text-xs uppercase tracking-wider text-amber-700 font-semibold">
              Still to be packed
            </span>
          }
        >
          <div className="overflow-x-auto">
            <table
              className="w-full min-w-[640px]"
              data-testid="eod-unpacked-table"
            >
              <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="py-2">Batch</th>
                  <th>Product</th>
                  <th className="text-right">Planned</th>
                  <th className="text-right">Produced</th>
                  <th>Stage</th>
                </tr>
              </thead>
              <tbody>
                {unpackedList.map((b) => (
                  <tr
                    key={b.id}
                    className="border-t border-border h-12"
                    data-testid={`eod-unpacked-row-${b.id}`}
                  >
                    <td className="font-mono text-sm">{b.batch_code || "—"}</td>
                    <td className="font-medium">{b.recipe_name}</td>
                    <td className="text-right tnum text-stone-600">
                      {Number(b.planned || 0).toFixed(2)}
                    </td>
                    <td className="text-right tnum font-semibold">
                      {Number(b.produced || 0).toFixed(2)}
                    </td>
                    <td>
                      <span className="inline-flex items-center px-2 h-6 rounded-full bg-amber-100 text-amber-900 text-[11px] font-bold uppercase tracking-wider">
                        {b.stage}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </>
  );
}
